import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Navigation } from "./components/Navigation"
import { MobileMenu } from "./components/MobileMenu"
import { LandingPage, LandingPageData } from "./components/LandingPage"
import Dashboard from "./components/internal/Dashboard"
import JsonData from "./data/data.json"
import { INTERNAL_TABS, isInternalView } from "./components/internal/tabs"
import { MessageModal, MessageState } from "./components/ui/components"
import { AUTH_EXPIRED_EVENT } from "./api/strapi"

// Centralized types
import { UserProfile, ActiveView } from "./types/objects"

// @ts-ignore
import SmoothScroll from "smooth-scroll"
import "./App.css"
import "./landing.css"

/**
 * Initialize Smooth Scrolling for anchors
 */
export const scroll = new SmoothScroll('a[href*="#"]', {
    speed: 1000,
    speedAsDuration: true
})

const App: React.FC = () => {
    /* --- Application State --- */

    const [user, setUser] = useState<UserProfile | null>(() => {
        const storedUser = localStorage.getItem("user")
        if (storedUser) {
            try {
                return JSON.parse(storedUser)
            } catch (e) {
                return null
            }
        }
        return null
    })

    const [view, setView] = useState<ActiveView | null>(() => {
        return localStorage.getItem("token") ? "portfolio" : null
    })
    
    const [landingPageData] = useState<LandingPageData>(JsonData as LandingPageData)
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false)
    const [notice, setNotice] = useState<MessageState | null>(null)

    /* --- Side Effects --- */

    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "unset"
        }
    }, [isMobileMenuOpen])

    /* --- Navigation Configurations --- */

    const activeSections = useMemo(() => {
        return user ?
            INTERNAL_TABS.map(tab => ({ id: tab.key, label: tab.label })) :
            [
                { id: "features", label: "Charakteristika" },
                { id: "about", label: "O nás" },
                { id: "services", label: "Služby" },
                { id: "products", label: "Produkty" },
                { id: "inquiry", label: "Poptávka" },
                { id: "pricing", label: "Ceník" },
                { id: "portfolio", label: "Galerie" },
                { id: "testimonials", label: "Reference" },
                { id: "team", label: "Tým" },
                { id: "contact", label: "Kontakt" },
                { id: "faq", label: "Časté dotazy" }
            ]
    }, [user])

    /* --- Handlers --- */

    const handleLogin = (u: UserProfile, token: string) => {
        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(u))
        setUser(u)
        setView("portfolio")
        setIsMobileMenuOpen(false)
    }

    const handleLogout = useCallback(() => {
        localStorage.removeItem("token")
        localStorage.removeItem("user")
        setUser(null)
        setView(null)
        setIsMobileMenuOpen(false)
        window.location.hash = ""
    }, [])

    // Any API call answered with 401 (expired / revoked JWT) logs the user out.
    useEffect(() => {
        const onExpired = () => {
            handleLogout()
            setNotice({ type: "info", title: "Přihlášení vypršelo", text: "Z bezpečnostních důvodů jste byli odhlášeni. Přihlaste se prosím znovu." })
        }
        window.addEventListener(AUTH_EXPIRED_EVENT, onExpired)
        return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired)
    }, [handleLogout])

    const handleNavigate = useCallback((v: any) => {
        if (user) {
            if (isInternalView(v)) {
                setView(v)
            }
        } else {
            setView(v)
        }
        setIsMobileMenuOpen(false)
    }, [user])

    const toggleMenu = useCallback((e?: React.MouseEvent | React.PointerEvent) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        setIsMobileMenuOpen(prev => !prev)
    }, [])

    const isUserInCrm = useMemo(() => !!(user && view), [user, view]);

    return (
        <div className="app-container">
            {/* 1. Persistent Navigation bar */}
            <Navigation
                user={user}
                setView={handleNavigate}
                onToggleMenu={toggleMenu}
                onLogin={handleLogin}
                onLogout={handleLogout}
            />

            {/* 2. Global Mobile Menu Overlay */}
            <MobileMenu
                sections={activeSections}
                activeSection={view as string}
                onSelect={(id) => {
                    if (id === null) {
                        setIsMobileMenuOpen(false)
                    } else {
                        handleNavigate(id)
                    }
                }}
                isOpen={isMobileMenuOpen}
                user={user}
                onLogin={handleLogin}
                onLogout={handleLogout}
            />

            {/* 3. Conditional View Rendering */}
            {isUserInCrm ? (
                <main style={{ paddingTop: "80px", minHeight: "100dvh", backgroundColor: "#f8fafc" }}>
                    <Dashboard user={user!} view={view || "portfolio"} onNavigate={setView} />
                </main>
            ) : (
                <LandingPage data={landingPageData} user={user} setView={handleNavigate} />
            )}

            <MessageModal message={notice} onClose={() => setNotice(null)} />
        </div>
    )
}

export default App;
